import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ADMIN_PLANS,
  planLabel,
  type AdminUserDetail,
  type AdminUserSummary,
  type Paginated,
} from "@currencycore/shared";
import { getUser, listUsers, unbanUser } from "../lib/api.ts";
import { FilterBar, type FilterDef, type SortDef } from "../components/FilterBar.tsx";
import { Pagination } from "../components/Pagination.tsx";
import { Drawer } from "../components/Drawer.tsx";
import { RowMenu } from "../components/RowMenu.tsx";
import { UserDetailBody } from "../components/UserDetailBody.tsx";
import { PlanBadge } from "../components/Badges.tsx";
import { toast } from "../components/Toast.tsx";
import { useDebounced, fmtDate, errMsg, filterParams } from "../lib/ui.ts";

const PAGE_SIZE = 20;

const FILTER_DEFS: FilterDef[] = [
  { key: "plan", label: "Plan", options: ADMIN_PLANS.map((p) => ({ value: p, label: planLabel(p) })) },
  {
    key: "banned",
    label: "Status",
    options: [
      { value: "false", label: "Active" },
      { value: "true", label: "Banned" },
    ],
  },
];

const SORT_DEFS: SortDef[] = [
  { value: "createdAt", label: "Created" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "orgs", label: "Orgs" },
];

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Paginated<AdminUserSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const debSearch = useDebounced(search);
  const navigate = useNavigate();

  useEffect(() => setPage(1), [debSearch, filters, sort, order]);

  function load() {
    return listUsers({ q: debSearch, page, pageSize: PAGE_SIZE, sort, order, ...filterParams(filters) });
  }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    load()
      .then((res) => alive && setData(res))
      .catch((e) => alive && toast(errMsg(e, "Failed to load users.")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debSearch, filters, sort, order, page]);

  const refresh = () => load().then(setData).catch(() => {});

  async function quickUnban(id: string) {
    try {
      await unbanUser(id);
      toast("User unbanned.", true);
      refresh();
    } catch (e) {
      toast(errMsg(e, "Could not unban."));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <p className="muted">{data ? `${data.total.toLocaleString("en-US")} total` : "Loading…"}</p>
        </div>
      </div>

      <FilterBar
        search={search}
        onSearch={setSearch}
        defs={FILTER_DEFS}
        filters={filters}
        onFilters={setFilters}
        sortDefs={SORT_DEFS}
        sort={sort}
        order={order}
        onSort={(s, o) => {
          setSort(s);
          setOrder(o);
        }}
        placeholder="Search by name, email, or ID…"
      />

      <div className="card table-card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Verified</th>
                <th className="col-num">Orgs</th>
                <th>Top plan</th>
                <th>Status</th>
                <th>Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={7} className="table-loading"><span className="spinner" /> Loading…</td></tr>
              ) : data && data.items.length === 0 ? (
                <tr><td colSpan={7} className="table-empty">No users match your filters.</td></tr>
              ) : (
                data?.items.map((u) => (
                  <tr key={u.id} className="clickable-row" onClick={() => setSelected(u.id)}>
                    <td>
                      <div className="cell-title">{u.name ?? "—"}</div>
                      <div className="cell-sub mono muted">{u.email}</div>
                    </td>
                    <td>
                      {u.emailVerified ? (
                        <span className="badge badge-ok">verified</span>
                      ) : (
                        <span className="badge badge-neutral">unverified</span>
                      )}
                    </td>
                    <td className="col-num tnum">{u.orgCount}</td>
                    <td>{u.topPlan ? <PlanBadge plan={u.topPlan} /> : <span className="muted">—</span>}</td>
                    <td>
                      {u.banned ? (
                        <span className="badge badge-danger" title={u.bannedReason ?? undefined}>banned</span>
                      ) : (
                        <span className="badge badge-ok">active</span>
                      )}
                    </td>
                    <td className="muted tnum">{fmtDate(u.createdAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <RowMenu
                        items={[
                          { label: "Quick view", onClick: () => setSelected(u.id) },
                          { label: "Open page", onClick: () => navigate(`/users/${u.id}`) },
                          { label: "Email user", onClick: () => navigate(`/emails?tab=send&userId=${u.id}`) },
                          ...(u.banned
                            ? [{ label: "Unban", onClick: () => quickUnban(u.id) }]
                            : []),
                          { label: "Copy ID", onClick: () => navigator.clipboard?.writeText(u.id) },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </div>

      <UserDrawer userId={selected} onClose={() => setSelected(null)} onMutated={refresh} />
    </>
  );
}

function UserDrawer({
  userId,
  onClose,
  onMutated,
}: {
  userId: string | null;
  onClose: () => void;
  onMutated: () => void;
}) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setDetail(null);
      return;
    }
    let alive = true;
    setLoading(true);
    getUser(userId)
      .then((d) => alive && setDetail(d))
      .catch((e) => alive && toast(errMsg(e, "Failed to load user.")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [userId]);

  return (
    <Drawer
      open={!!userId}
      onClose={onClose}
      title={detail?.name ?? (loading ? "Loading…" : "User")}
      subtitle={detail ? <span className="mono">{detail.email}</span> : undefined}
      footer={
        detail ? (
          <Link to={`/users/${detail.id}`} className="btn btn-ghost btn-sm" onClick={onClose}>
            Open full page →
          </Link>
        ) : undefined
      }
    >
      {!detail ? (
        <div className="drawer-loading"><span className="spinner" /> Loading…</div>
      ) : (
        <UserDetailBody
          detail={detail}
          onUpdated={(d) => {
            setDetail(d);
            onMutated();
          }}
          onDeleted={() => {
            onMutated();
            onClose();
          }}
        />
      )}
    </Drawer>
  );
}
